import { isJwAuthError } from '@jwplayer/companion-gateway';
import { Component, useEffect, useState } from 'react';

import { SomethingWentWrongError } from '/component/container/page/error/states/something-went-wrong';
import { createSession } from '/global-state/session/actions';
import { getSentry } from '/module/sentry';

import { OuterChrome } from '../component/chrome/outer';

import { Providers } from './providers';
import { Router } from './router';

function requestAnimationFrameOrTimeout(time = 100) {
  return Promise.race([
    new Promise((resolve) => window.requestAnimationFrame(resolve)),
    new Promise((resolve) => setTimeout(resolve, time)),
  ]);
}

function transitionEndOrTimeout(elm: Element, time = 100) {
  return Promise.race([
    new Promise((resolve) => {
      const handler = () => {
        elm.removeEventListener('transitionend', handler);
        resolve(undefined);
      };

      elm.addEventListener('transitionend', handler);
    }),
    new Promise((resolve) => setTimeout(resolve, time)),
  ]);
}

export const cleanupRootLoader = (() => {
  // Prevent function from running twice
  let loaderElementRemoved = false;

  function removeElm(elm: Element) {
    if (loaderElementRemoved) {
      return;
    }

    try {
      elm.remove();
      loaderElementRemoved = true;
    } catch {
      // ignore
    }
  }

  return () => {
    if (loaderElementRemoved) {
      return;
    }

    const loaderElement = document.getElementById('jw-dash-root-loader');
    if (!loaderElement) {
      return;
    }

    requestAnimationFrameOrTimeout().then(() => {
      transitionEndOrTimeout(loaderElement).then(() => {
        removeElm(loaderElement);
      });

      // Start CSS animations/transitions through this class
      loaderElement.classList.add('-remove');
    });
  };
})();

function useAppInit() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError] = useState<Error | unknown | undefined>(undefined);

  useEffect(() => {
    if (!isLoading) {
      // App has already been initialized
      return;
    }

    (async () => {
      try {
        // Try to resume an existing user session
        await createSession();
      } catch (err: unknown) {
        if (isJwAuthError(err) && err.code === 'MISSING_TOKEN') {
          // An error here means that we couldn't resume a session, so a login screen needs to show
          return;
        }
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isLoading]);

  return { isLoading, hasError };
}

function InnerApp() {
  const { isLoading, hasError } = useAppInit();

  // Clean up the root loader after a timeout for nice UI
  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => cleanupRootLoader(), 50);
    }
  }, [isLoading]);

  if (isLoading) {
    return null;
  }

  if (hasError) {
    cleanupRootLoader();
    throw hasError;
  }

  return (
    <OuterChrome>
      <Router />
    </OuterChrome>
  );
}

/**
 * Main React app component
 */
export class MainApp extends Component {
  state: {
    hasError?: Error;
  } = {
    hasError: undefined,
  };

  static getDerivedStateFromError(error?: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: error };
  }

  componentDidCatch(error?: Error, errorInfo?: React.ErrorInfo) {
    console.error(error);

    getSentry().then((sentry) => {
      if (!error || !sentry) {
        return;
      }

      errorInfo && sentry.setExtras(errorInfo as unknown as Record<string, unknown>);
      error.name = `MainAppError: ${error.name}`;
      sentry.captureException(error);
    });
  }

  render() {
    const { hasError } = this.state;

    if (hasError) {
      // If error occured during app load, ensure the loader is cleaned up as well
      cleanupRootLoader();

      return <SomethingWentWrongError />;
    }
    return (
      <Providers>
        <InnerApp />
      </Providers>
    );
  }
}
